// Web component: nox-carousel

class AnimationScheduler {
	constructor(fn, time) {
		this.cancelled = false;
		this.fn = fn;
		this.time = time;
	}

	start() {
		let start = undefined;
		const step = () => {
			return new Promise(resolve => {
				requestAnimationFrame((frame) => {
					if (!this.cancelled) {
						if (start === undefined) {
							start = frame;
						}
						let t = Math.min((frame - start) / this.time, 1);
						resolve(t);
					}
				});
			});
		}

		return this.fn(step);
	}

	stop() {
		this.cancelled = true;
	}
}

class ResetableInterval {
	constructor(fn, delay) {
		this.fn = fn;
		this.delay = delay;
		this.timer = undefined;
		this.start();
	}

	reset() {
		if (this.timer && this.delay) {
			clearInterval(this.timer);
			this.timer = setInterval(() => {
				this.fn();
			}, this.delay * 1000);
		}
	}

	stop() {
		if (this.timer) {
			clearInterval(this.timer);
			this.timer = undefined;
		}
	}

	start() {
		if (!this.timer && this.delay) {
			this.timer = setInterval(() => {
				this.fn();
			}, this.delay * 1000);
		}
	}
}

class DragHandler {
	constructor(element, fn) {
		element.addEventListener("pointerdown", (event) => {
			if (window.innerWidth < 1200) {
				return;
			}
			event.preventDefault();
			let startX = event.clientX;
			let lastX = startX;
			let dragging = false;

			const onPointerMove = (moveEvent) => {
				moveEvent.preventDefault();
				let deltaX = moveEvent.clientX - lastX;
				if (Math.abs(moveEvent.clientX - startX) < 3) {
					return; // ignore small movements
				}
				lastX = moveEvent.clientX;
				fn(deltaX);

				if (!dragging) {
					dragging = true;
					element.querySelectorAll("*").forEach(el => {
						el.dispatchEvent(new CustomEvent("nox-carousel-drag-start", {
							bubbles: false,
							composed: true
						}));
					});
				}
			};

			const onPointerUp = (upEvent) => {
				upEvent.preventDefault();
				window.removeEventListener("pointermove", onPointerMove);
				window.removeEventListener("pointerup", onPointerUp);
				fn(undefined);

				if (dragging) {
					dragging = false;
					setTimeout(() => {
						element.querySelectorAll("*").forEach(el => {
							el.dispatchEvent(new CustomEvent("nox-carousel-drag-end", {
								bubbles: false,
								composed: true
							}));
						});
					}, 1);
				}
			};

			window.addEventListener("pointermove", onPointerMove);
			window.addEventListener("pointerup", onPointerUp);
		});
	}
}

class NoxCarousel extends HTMLElement {
	constructor() {
		super();
		this.dots = [];
		this.items = [];

		this.currentIndex = 0;
		this.currentOffset = 0;
		this.targetIndex = 0;
		this.targetOffset = 0;

		this._isDragging = false;
		this.animation = undefined;
		this.timers = [];
	}

	get isDragging() {
		return this._isDragging;
	}

	set isDragging(value) {
		this._isDragging = value;
		this.timers.forEach(timer => {
			if (this._isDragging) {
				timer.stop();
			} else {
				timer.start();
			}
		});
	}

	connectedCallback() {
		let optionsAnimate = this.getAttribute("carousel-animate");
		if (optionsAnimate === "playback-ended") {
			this.optionsAnimate = optionsAnimate;
			this.addEventListener(optionsAnimate, () => {
				if (!this.isDragging) {
					this.navigateRight();
				}
			});
		} else if (optionsAnimate && !isNaN(parseFloat(optionsAnimate))) {
			this.optionsAnimate = parseFloat(optionsAnimate);
			this.timers.push(new ResetableInterval(() => {
				this.navigateRight();
			}, this.optionsAnimate));
		}

		this.container = document.createElement("div");
		this.container.classList.add("nox-container");
		this.appendChild(this.container);

		this.wrapper = document.createElement("div");
		this.wrapper.classList.add("nox-wrapper");
		this.container.appendChild(this.wrapper);

		this.itemsContainer = document.createElement("div");
		this.itemsContainer.classList.add("nox-items");
		this.wrapper.appendChild(this.itemsContainer);

		this.controls = document.createElement("div");
		this.controls.classList.add("nox-controls");
		this.appendChild(this.controls);

		this.dotsContainer = document.createElement("div");
		this.dotsContainer.classList.add("nox-dots");
		this.appendChild(this.dotsContainer);

		this.controlLeft = document.createElement("button");
		this.controlLeft.classList.add("nox-control", "nox-control-left");
		this.controlLeft.setAttribute("aria-label", "Previous");
		this.controlLeft.innerHTML = "&#8249;";
		this.controlLeft.addEventListener("click", () => { this.navigateLeft(); });
		this.controls.appendChild(this.controlLeft);

		this.controlRight = document.createElement("button");
		this.controlRight.classList.add("nox-control", "nox-control-right");
		this.controlRight.setAttribute("aria-label", "Next");
		this.controlRight.innerHTML = "&#8250;";
		this.controlRight.addEventListener("click", () => { this.navigateRight(); });
		this.controls.appendChild(this.controlRight);


		// Create an observer to watch for future child additions
		this.observer = new MutationObserver(this.handleMutations.bind(this));
		this.observer.observe(this, { childList: true, subtree: false });

		requestAnimationFrame(() => {
			this.itemsContainer.style.visibility = "visible";
		});

		this.addEventListener("item-selected", (event) => {
			console.log("Item selected event:", event);
			let sourceElement = event.target;
			// figure out which div the item is
			let selectedItem = sourceElement.closest("nox-carousel > .nox-container > .nox-wrapper > .nox-items > *");
			let targetIndex = this.items.indexOf(selectedItem);
			console.log("Item selected:", selectedItem, targetIndex);
			if (targetIndex !== -1) {
				this.navigate(targetIndex);
			}
		});

		// grab all existing children and move them to itemsContainer
		let elements = this.querySelectorAll(":scope > *");
		elements.forEach(el => {
			if (el !== this.container && el !== this.controls && el !== this.dotsContainer) {
				this.itemsContainer.appendChild(el);
			}
		});

		// Initial processing of children
		this.processChildren();

		new DragHandler(this.container, (deltaX) => {
			if (deltaX === undefined) {
				// drag ended
				setTimeout(() => { this.isDragging = false; }, 1);
				this.animateTo(this.currentIndex, 0);
				return;
			}
			if (this.animation) {
				this.animation.stop();
			}
			this.isDragging = true;
			let currentPosition = this.itemPositionToPosition(this.currentIndex, this.currentOffset);
			currentPosition -= deltaX;
			let [newIndex, newOffset] = this.positionToItemPosition(currentPosition);
			this.setPosition(newIndex, newOffset);
		});

		// prevent links from working during drag
		this.container.addEventListener("click", (event) => {
			if (this.isDragging) {
				event.preventDefault();
				event.stopPropagation();
			}
		}, true);

		window.addEventListener("resize", () => {
			this.animateToItem(this.currentIndex, true);
		});
	}

	navigate(target) {
		this.centerItem(target);
	}

	navigateLeft() {
		this.navigateRelative(-1);
	}

	navigateRight() {
		this.navigateRelative(1);
	}

	navigateRelative(offset) {
		let target = (this.targetIndex + offset + this.items.length) % this.items.length;
		this.navigate(target);
	}

	disconnectedCallback() {
		if (this.observer) {
			this.observer.disconnect();
		}
	}

	handleMutations(mutations) {
		let updated = false;
		for (const mutation of mutations) {
			if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
				updated = true;
				mutation.addedNodes.forEach(node => {
					if (node.nodeType === Node.ELEMENT_NODE) {
						this.itemsContainer.appendChild(node);
					}
				});
			}
		}
		if (updated) {
			this.requestUpdate();
		}
	}

	requestUpdate() {
		if (this.updateRequest) {
			return;
		}
		this.updateRequest = requestAnimationFrame(() => {
			this.updateRequest = undefined;
			this.processChildren();
		});
	}

	processChildren() {
		this.items = [...this.itemsContainer.querySelectorAll(":scope > *")];

		this.updateDots(2);
		this.centerItem(2, true);
	}

	updateDots(current) {
		let maxDots = Math.max(this.items.length, this.dots.length);
		for (let i = 0; i < maxDots; i++) {
			let dot = this.dots[i];
			if (!dot && i < this.items.length) {
				dot = document.createElement("div");
				dot.classList.add("nox-dot");
				if (i === current) {
					dot.classList.add("active");
				}
				this.dotsContainer.appendChild(dot);
				this.dots.push(dot);
				dot.addEventListener("click", () => {
					this.navigate(i);
				});
			} else if (dot && i >= this.items.length) {
				this.dotsContainer.removeChild(dot);
				this.dots.splice(i, 1);
			}
		}
	}

	/** Distance between items in circular manner */
	distance(a, b) {
		let direct = Math.abs(a - b);
		let wrapAround = this.items.length - direct;
		return Math.min(direct, wrapAround);
	}

	reorderItems(centerIndex) {
		// Move element so that centerIndex is in the middle
		let half = Math.floor(this.items.length / 2);

		this.items.forEach((item, index) => {
			let newIndex = index - centerIndex;
			if (newIndex > half) {
				newIndex -= this.items.length;
			} else if (newIndex < -half) {
				newIndex += this.items.length;
			}
			item.style.order = newIndex;
		});
	}

	centerItem(index, initial = false) {
		this.reorderItems(index);
		this.animateToItem(index, initial);
	}

	animateToItem(index, initial = false) {
		if (this.items.length === 0) {
			return;
		}
		if (initial) {
			this.setPosition(index, 0, initial);
			return;
		}

		this.animateTo(index, 0, initial);
	}

	animateTo(index, offset, initial = false) {
		this.targetIndex = index;
		this.targetOffset = offset;

		let currentPosition = this.itemPositionToPosition(this.currentIndex, this.currentOffset);
		let targetPosition = this.itemPositionToPosition(index, offset);

		let distance = targetPosition - currentPosition;
		let totalWidth = this.items.reduce((sum, item) => sum + item.offsetWidth, 0);
		if (distance > totalWidth / 2) {
			distance -= totalWidth;
		} else if (distance < -totalWidth / 2) {
			distance += totalWidth;
		}
		targetPosition = currentPosition + distance;

		if (this.animation) {
			this.animation.stop();
		}

		this.animation = new AnimationScheduler(async (step) => {
			while (true) {
				let t = await step();
				let easedT = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
				let intermediatePosition = currentPosition + (targetPosition - currentPosition) * easedT;
				let [intermediateIndex, intermediateOffset] = this.positionToItemPosition(intermediatePosition);
				this.setPosition(intermediateIndex, intermediateOffset, initial);
				if (t >= 1) { break; }
			}
			// ensure final position
			this.setPosition(index, 0, initial);
		}, 500);
		this.animation.start();
	}

	positionToItemPosition(x) {
		let sizes = this.items.map(item => item.offsetWidth);
		let totalWidth = sizes.reduce((a, b) => a + b, 0);
		let count = this.items.length;

		x = (((x % totalWidth) + sizes[0] / 2) + totalWidth) % totalWidth;

		let offset = 0;
		for (let i = 0; i < count; i++) {
			let size = sizes[i];
			if (x <= offset + size) { return [i, x - offset - size / 2]; }
			offset += size;
		}
	}

	itemPositionToPosition(index, offset) {
		let sizes = this.items.map(item => item.offsetWidth);
		let position = 0;
		for (let i = 0; i <= index - 1; i++) { position += sizes[i]; }
		return position - (sizes[0] / 2) + (sizes[index] / 2) + offset;
	}

	clipPosition(x) {
		let sizes = this.items.map(item => item.offsetWidth);
		let totalWidth = sizes.reduce((a, b) => a + b, 0);
		return (((x % totalWidth) + (sizes[0] / 2) + totalWidth) % totalWidth) - (sizes[0] / 2);
	}

	setPosition(index, offset, initial = false) {
		this.reorderItems(index);
		this.currentIndex = index;
		this.currentOffset = offset;

		if (initial) {
			this.targetIndex = index;
			this.targetOffset = offset;
		}

		let centerItem = this.items[index];
		let containerCenter = centerItem.offsetLeft;

		let position = containerCenter + (centerItem.offsetWidth / 2) + offset;

		this.itemsContainer.style.transform = `translateX(${-position}px)`;
		// Force reflow to apply the transform without transition on initial setup
		if (initial) {
			getComputedStyle(this.itemsContainer).transform;
		}

		if (!initial) {
			this.dots.forEach((dot, dotIndex) => {
				dot.classList.toggle("active", dotIndex === index);
			});
		}

		this.items.forEach((item, itemIndex) => {
			let active = itemIndex === index;
			let distance = this.distance(itemIndex, index);
			let side = distance === 1;

			item.classList.toggle("item-current", active);
			item.classList.toggle("item-side", side);

			let offsetDistance = Math.abs(item.offsetLeft + (item.offsetWidth / 2) - position);
			let viewportWidth = this.container.offsetWidth;
			let scale = Math.max(0.9, 1 - (offsetDistance / viewportWidth * 0.1));
			item.style.setProperty("scale", scale);

			// send custom event
			if (active) {
				item.querySelectorAll("*").forEach(el => {
					el.dispatchEvent(new CustomEvent("nox-carousel-active", { bubbles: false, composed: true }));
				});
			} else {
				item.querySelectorAll("*").forEach(el => {
					el.dispatchEvent(new CustomEvent("nox-carousel-inactive", { bubbles: false, composed: true }));
				});
			}
		});
	}

}
customElements.define("nox-carousel", NoxCarousel);
